import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/useAuth";

export default function Login() {
  const { signIn, isAuthenticated, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && isAuthenticated) navigate("/", { replace: true });
  }, [loading, isAuthenticated, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await signIn(email.trim(), password);
      navigate("/", { replace: true });
    } catch (ex) {
      const detail = ex?.response?.data?.detail || ex?.message || "Sign-in failed";
      const msg = typeof detail === "string" ? detail : JSON.stringify(detail);
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#05050A] px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <div className="inline-flex items-center gap-2">
            <div className="w-2 h-2 bg-[#00E676] pulse-dot" />
            <span className="font-semibold tracking-tight text-sm">MONEY PRINTER V1</span>
          </div>
          <div className="text-[10px] uppercase tracking-widest text-[#71717A] mt-1">
            single-admin access
          </div>
        </div>

        <div className="bg-[#0F0F14] border border-border rounded-sm">
          <div className="px-6 py-4 border-b border-border">
            <div className="text-xs uppercase tracking-widest text-white">Sign in</div>
            <div className="text-[10px] text-[#71717A] mt-1">
              Operator-only console. New accounts cannot be created here.
            </div>
          </div>

          <form onSubmit={submit} data-testid="login-form" className="p-6 space-y-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-[#A0A0AB]">Email</Label>
              <Input
                data-testid="login-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-[#1A1A22] border-border rounded-sm font-mono"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-[#A0A0AB]">Password</Label>
              <Input
                data-testid="login-password"
                type="password"
                autoComplete="current-password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-[#1A1A22] border-border rounded-sm font-mono"
              />
            </div>

            {err ? (
              <div
                data-testid="login-error"
                className="text-sm leading-relaxed border border-[#FF3B30] bg-[#FF3B30]/15 text-[#FF7A6E] rounded-sm px-3 py-3"
                role="alert"
              >
                <div className="font-semibold uppercase tracking-wider text-[10px] mb-1 text-[#FF3B30]">
                  Error
                </div>
                {err}
              </div>
            ) : null}

            <Button
              data-testid="login-submit"
              type="submit"
              disabled={busy}
              className={`w-full rounded-sm transition-colors ${
                busy ? "bg-[#1A1A22] text-[#A0A0AB] cursor-wait" : "bg-white text-black hover:bg-[#A0A0AB]"
              }`}
            >
              {busy ? (
                <span className="inline-flex items-center gap-2">
                  <span className="inline-block w-3 h-3 border-2 border-[#A0A0AB] border-t-white rounded-full animate-spin" />
                  Signing in…
                </span>
              ) : (
                "Sign in"
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
